import Component from '@glimmer/component'
import { service } from '@ember/service'
import { reportingLegend } from 'district-ui-client/utils/giraffe/highcharts-config'
import convertDateFormat from 'district-ui-client/utils/giraffe/convert-date-format'
import { formatNumber } from 'district-ui-client/utils/giraffe/formatter'
import { colors } from '@colors'
import type { IntlService } from 'ember-intl'
import type { ReportingProductSlug } from 'district-ui-client/domain/product'
import { SeriesColumn } from 'district-ui-client/components/primitives/series-column/component'
import { LeftSplitChart } from 'district-ui-client/components/left-split-chart'
import { SummaryBox, SummaryBoxColorScheme } from 'district-ui-client/components/summary-box'
import { eq } from 'ember-truth-helpers'
import type { SeriesColumnOptions } from 'highcharts'
import type { AwardsEarnedTotalsByGradeData } from 'district-ui-client/utils/student-events/fetchers/awards-earned-totals'
import type { Stat } from 'district-ui-client/components/figure-boxes'

/**
 * Internal Component called by awards-earned/certificates/totals and
 * awards-earned/individual. Renders the series-column chart for certificates
 * earned; Mathseeds variant.
 *
 * Data format:
 *
 * ```
 * {
 *   grade_position: 1,
 *   summary: [
 *     { category: 'gold', count: 90 },
 *     { category: 'silver', count: 123 },
 *     { category: 'bronze', count: 250 },
 *   ],
 *   months: [
 *     {
 *       gold: 20,
 *       silver: 23,
 *       bronze: 125,
 *       month: '2016-07',
 *     },
 *     {
 *       gold: 70,
 *       silver: 100,
 *       bronze: 125,
 *       month: '2016-08',
 *     },
 *   ],
 * ```
 */

interface Signature {
  Args: {
    data: AwardsEarnedTotalsByGradeData
    product: ReportingProductSlug
    yAxisTitle?: string
    grade?: string
    title?: string
  }
  Element: HTMLDivElement
}

export class ChartsAwardsEarnedCertificatesByMonth extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Computed list of summary statistics, extracted from the input data.
   * Display total gold, silver and bronze counts.
   */
  get summaryStats(): Stat[] {
    const { intl } = this
    const { gold, silver, bronze } = this.args.data.summary

    const goldLabel = intl.t(`reporting.charts.awardsEarned.certificates.byMonth.goldLabel.${this.args.product}`)
    const silverLabel = intl.t(`reporting.charts.awardsEarned.certificates.byMonth.silverLabel.${this.args.product}`)
    const bronzeLabel = intl.t(`reporting.charts.awardsEarned.certificates.byMonth.bronzeLabel.${this.args.product}`)
    const totalLabel = intl.t(`reporting.charts.awardsEarned.certificates.totalLabel`)

    const total = gold + silver + bronze

    return [
      { label: goldLabel, value: formatNumber(gold, 0) },
      { label: silverLabel, value: formatNumber(silver, 0) },
      { label: bronzeLabel, value: formatNumber(bronze, 0) },
      { label: totalLabel, value: formatNumber(total, 0) },
    ]
  }

  /**
   * The student data transformed to be rendered by highcharts.
   * Made up of three series of data, one for each certificate colour.
   */
  get chartData(): SeriesColumnOptions[] {
    return [
      {
        name: this.intl.t(`reporting.charts.awardsEarned.certificates.byMonth.goldLabel.${this.args.product}`),
        data: this.args.data?.months?.map((m) => m.gold) ?? [],
        color: colors.medalGold,
        legendIndex: 3,
        type: 'column',
      },
      {
        name: this.intl.t(`reporting.charts.awardsEarned.certificates.byMonth.silverLabel.${this.args.product}`),
        data: this.args.data?.months?.map((m) => m.silver) ?? [],
        color: colors.medalSilver,
        legendIndex: 2,
        type: 'column',
      },
      {
        name: this.intl.t(`reporting.charts.awardsEarned.certificates.byMonth.bronzeLabel.${this.args.product}`),
        data: this.args.data?.months?.map((m) => m.bronze) ?? [],
        color: colors.medalBronze,
        legendIndex: 1,
        type: 'column',
      },
    ]
  }

  /**
   * Computed list of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories() {
    return this.args.data?.months?.map((m) => convertDateFormat(m.month)) ?? []
  }

  get legend() {
    return reportingLegend('left', 'top', 30, -15)
  }

  <template>
    <div class="relative" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{if (eq @product "maths") SummaryBoxColorScheme.DarkGreen SummaryBoxColorScheme.LightBlue}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @data={{this.chartData}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @legend={{this.legend}}
            @yAxisLabel={{@yAxisTitle}}
            @title={{@title}}
            @chartMargin={{Array 60 10 30 55}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default ChartsAwardsEarnedCertificatesByMonth
